import { MouseEvent, useCallback } from 'react';

import {
    Box,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    useTheme,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import {
    gridPageCountSelector,
    gridPageSelector,
    gridPageSizeSelector,
    gridRowCountSelector,
    useGridApiContext,
    useGridSelector,
} from '@mui/x-data-grid-pro';

interface CustomFooterProps {
    pageSizeOptions: number[];
}

function CustomFooter({ pageSizeOptions }: CustomFooterProps) {
    const theme = useTheme();
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
    const rowCount = useGridSelector(apiRef, gridRowCountSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    const handlePageChange = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        apiRef.current.setPage(newPage - 1);
    };

    const handlePageSizeChange = useCallback(
        (event: SelectChangeEvent<number>) => {
            const newSize = Number(event.target.value);
            apiRef.current.setPageSize(newSize);
            apiRef.current.setPage(0);
        },
        [apiRef]
    );

    const showingCount = Math.min((page + 1) * pageSize, rowCount);

    const outlineColor = theme.palette.orange.main;
    const backgroundHoverColor = theme.palette.background.lightOrange;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                py: 1,
                px: 2,
                backgroundColor: 'transparent',
            }}
        >
            {/* Left: Showing text */}
            <Typography variant="body2" color="black" data-testid="npi-datagrid-showing-label">
                {`Showing`}{' '}
                <Typography component="span" fontWeight={600}>
                    {showingCount}
                </Typography>{' '}
                {`of ${rowCount}`}
            </Typography>

            {/* Middle: Page numbers */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MuiPagination
                    color="secondary"
                    count={pageCount}
                    page={page + 1}
                    onChange={handlePageChange}
                    size="small"
                    sx={{
                        '& .MuiPaginationItem-root': {
                            fontSize: 14,
                            fontWeight: 600,
                            borderRadius: 'rounded',
                            color: 'black',
                            transition: 'background-color 0.15s ease, color 0.15s ease',
                        },
                        '& .MuiPaginationItem-root:hover': {
                            backgroundColor: backgroundHoverColor,
                        },
                        '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: outlineColor,
                            color: 'white',
                        },
                        '& .MuiPaginationItem-root.Mui-selected:hover': {
                            backgroundColor: outlineColor,
                            color: 'white',
                        },
                        '.MuiPaginationItem-previousNext': {
                            backgroundColor: theme.palette.grey[300],
                        },
                        '.MuiPaginationItem-previousNext:hover': {
                            backgroundColor: backgroundHoverColor,
                        },
                    }}
                />
            </Box>

            {/* Right: Page size selector */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl sx={{ mr: 0 }}>
                    <Select<number>
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        renderValue={(val: number) => {
                            return `${val}`;
                        }}
                        MenuProps={{
                            anchorOrigin: { vertical: 'top', horizontal: 'center' },
                            transformOrigin: { vertical: 'bottom', horizontal: 'center' },
                            PaperProps: {
                                elevation: 8,
                                sx: {
                                    borderRadius: 3,
                                    mt: 0,
                                    py: 0,
                                    boxShadow: '0px 8px 24px rgba(0,0,0,0.18)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    '& .MuiMenuItem-root': {
                                        fontSize: 14,
                                        justifyContent: 'center',
                                        fontWeight: 600,
                                        position: 'relative',
                                        '&:not(:last-of-type)::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: '15%',
                                            bottom: 0,
                                            width: '70%',
                                            height: '1px',
                                            backgroundColor: theme.palette.grey[200],
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: `${outlineColor} !important`,
                                            color: 'white',
                                        },
                                    },
                                    '& .MuiMenuItem-root:hover': {
                                        backgroundColor: `${backgroundHoverColor} !important`,
                                        color: 'black',
                                    },
                                },
                            },
                        }}
                        sx={{
                            width: 55,
                            '& .MuiOutlinedInput-root': {
                                height: 30,
                                minHeight: 30,
                                borderRadius: 9999,
                                backgroundColor: theme.palette.grey[300],
                                paddingLeft: '10px',
                                paddingRight: '24px',
                                boxShadow: 'none',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: '0 !important',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    border: '0 !important',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: '0 !important',
                                },
                            },
                            '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                height: '100% !important',
                                lineHeight: '30px',
                                paddingLeft: '13px',
                                backgroundColor: theme.palette.grey[300],
                                borderRadius: 9999,
                                fontWeight: 600,
                            },
                            '& .MuiSelect-select:focus': {
                                backgroundColor: theme.palette.grey[300],
                                borderRadius: 9999,
                            },
                            '& .MuiSvgIcon-root': {
                                color: 'black',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused': {
                                backgroundColor: theme.palette.grey[300],
                            },
                        }}
                    >
                        {pageSizeOptions.map((size: number) => {
                            return (
                                <MenuItem key={size} value={size} sx={{ justifyContent: 'center' }}>
                                    {size}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <Typography ml={1} variant="body2" color="black">
                    / page
                </Typography>
            </Box>
        </Box>
    );
}

export default CustomFooter;
